import React from 'react';
import { CustomerSearch, CustomerCard } from '@features/customers';
import { useQuote } from '../../context/QuoteContext';
import type { Customer } from '@features/customers/types/customers.types';
import { Alert, PageHeader } from '@/shared/components';

interface CustomerSelectionStepProps {
    /** Flushes the quote's save lane; it rejects after the lane has surfaced the failure. */
    onSaveQuote: () => Promise<unknown>;
    /** Required, not optional: a permissive default would send a save for an incomplete quote. */
    canSaveQuote: boolean;
}

export const CustomerSelectionStep: React.FC<CustomerSelectionStepProps> = ({
    onSaveQuote,
    canSaveQuote,
}) => {
    const { selectedCustomer, selectCustomer } = useQuote();

    const handleCustomerSelect = (customer: Customer | null) => {
        selectCustomer(customer);
    };

    // The merged customer is committed to the slice first; the lane reads it when its run starts.
    // A not-yet-saveable quote is success, so EditCustomerForm closes cleanly.
    const handleSavePending = async (merged: Customer) => {
        selectCustomer(merged);
        if (!canSaveQuote) return;
        await onSaveQuote();
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between content-start items-start">
                <PageHeader
                    title="Confirm Customer Details"
                    subtitle={`Please review the customer details and click Next Step to continue`}
                />
            </div>

            {selectedCustomer ? (
                <CustomerCard
                    customer={selectedCustomer}
                    mode="selected"
                    onSelectCustomer={() => {}}
                    onClearSelection={() => handleCustomerSelect(null)}
                    onCustomerUpdated={handleCustomerSelect}
                    onSavePending={handleSavePending}
                />
            ) : (
                <>
                    <CustomerSearch
                        onSelectCustomer={handleCustomerSelect}
                        selectedCustomer={selectedCustomer}
                    />
                    <Alert variant="warning" role="note">
                        Please select a customer to continue to the next step.
                    </Alert>
                </>
            )}
        </div>
    );
};
